import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { type DeepPartial, type FieldErrors } from 'react-hook-form'
import { toast } from 'sonner'

import { upsertCaseAlertMutation } from '@/lib/queries/business/v1/alert/mutations'
import { getCaseAlertListQueryKey } from '@/lib/queries/business/v1/alert/queries'
import { type CaseAlertTimeUnit, type ReqUpsertCaseAlertMutation } from '@/lib/queries/business/v1/alert/types'
import { getUtcDateString } from '@/lib/utils/datetime'

import { useFindTargetCase } from '../../../../hooks/use-find-target-case'
import { caseAlertFormSchemaRequired } from '../../constants'
import { type CaseAlertForm, type CaseAlertFormRequired, type CaseAlertFormSchema } from '../../types'
import { type ContentProps } from './content'

export type UseContentParams = Pick<ContentProps, 'closeDialog' | 'isEoir'>

export const useContent = ({ isEoir = false, closeDialog }: UseContentParams) => {
  const { targetCase } = useFindTargetCase({ isEoir })
  const caseId = targetCase?.case_id

  const [alertSettingsDataList, setAlertSettingsDataList] = useState<CaseAlertForm[]>([
    {
      id: crypto.randomUUID(),
      alertType: '',
      alertDate: undefined,
      notificationSettings: {
        count: '',
        unit: '',
      },
      errors: {},
    },
  ])

  const { mutateAsync: upsertCaseAlerts, isPending: isPendingUpsertCaseAlerts } = useMutation(upsertCaseAlertMutation)

  const addAlertSettingsItem = () => {
    setAlertSettingsDataList((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        alertType: '',
        alertDate: undefined,
        notificationSettings: {
          count: '',
          unit: '',
        },
        errors: {},
      },
    ])
  }

  const updateAlertSettingsDataList = (value: CaseAlertFormSchema, index: number) => {
    setAlertSettingsDataList((prev) => {
      const newItems = [...prev]
      newItems[index] = { ...newItems[index], ...value }
      return newItems
    })
  }

  const deleteAlertSettingsItem = (id: string) => {
    setAlertSettingsDataList((prev) => prev.filter((item) => item.id !== id))
  }

  const updateAlertSettingErrorList = (errors: FieldErrors<DeepPartial<CaseAlertFormSchema>>, index: number) => {
    setAlertSettingsDataList((prev) => {
      const newItems = [...prev]
      newItems[index] = { ...newItems[index], errors }
      return newItems
    })
  }

  const handleClickSetAlertButton = async () => {
    if (!caseId) {
      return
    }

    const isAllValidItem = alertSettingsDataList.every((item) => caseAlertFormSchemaRequired.safeParse(item).success)
    if (!isAllValidItem) {
      return
    }

    const payload = constructCaseAlertUpsertPayload(caseId, alertSettingsDataList as CaseAlertFormRequired[])
    await upsertCaseAlerts(payload, {
      onSuccess: () => {
        toast.success('Alert settings registered successfully.')
        setTimeout(() => {
          void queryClient.invalidateQueries({
            queryKey: getCaseAlertListQueryKey(caseId),
          })
        }, 2000)
      },
      onError: () => {
        toast.error('Failed to register alert settings.')
      },
    })

    closeDialog()
  }

  const constructCaseAlertUpsertPayload = (
    caseId: number,
    dataList: CaseAlertFormRequired[]
  ): ReqUpsertCaseAlertMutation => {
    return dataList.map(({ alertType, alertDate, notificationSettings }) => ({
      alertType,
      caseId,
      deadline: getUtcDateString(alertDate),
      timeAgo: +notificationSettings.count,
      timeUnit: notificationSettings.unit as CaseAlertTimeUnit,
    }))
  }

  const isValidData = (settingsData: CaseAlertForm) => {
    const { alertType, alertDate, notificationSettings } = settingsData
    if (alertType && alertDate && notificationSettings?.count && notificationSettings?.unit) {
      return true
    }
    return false
  }

  const hasErrors = (settingsData: CaseAlertForm) => {
    return Object.keys(settingsData.errors).length > 0
  }

  const notExistData = alertSettingsDataList.length === 0
  const isValidAlertSettingsDataList = alertSettingsDataList.every(
    (settingsData) => isValidData(settingsData) && !hasErrors(settingsData)
  )
  const isDisabledSetAnotherAlertButton = isPendingUpsertCaseAlerts
  const isDisabledSetAlertButton = !isValidAlertSettingsDataList || isPendingUpsertCaseAlerts || notExistData

  return {
    alertSettingsDataList,
    isDisabledSetAlertButton,
    isDisabledSetAnotherAlertButton,
    isPendingUpsertCaseAlerts,
    updateAlertSettingsDataList,
    addAlertSettingsItem,
    deleteAlertSettingsItem,
    updateAlertSettingErrorList,
    handleClickSetAlertButton,
  }
}
